import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const NAV = [
  { key: "service", href: "#service" },
  { key: "howItWorks", href: "#how" },
  { key: "advantages", href: "#advantages" },
  { key: "faq", href: "#faq" },
];

export default function Header() {
  const { t, lang, prefix, switchTo } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isHome =
    location.pathname === "/ec" ||
    location.pathname === "/en" ||
    location.pathname === "/ec/" ||
    location.pathname === "/en/";

  const anchorHref = (hash) => (isHome ? hash : `${prefix}${hash}`);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full bg-slate-900 text-slate-100 border-b border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            to={prefix}
            data-testid="nav-logo"
            className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight"
            onClick={closeMenu}
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-teal-500 text-slate-950 font-extrabold">
              C
            </span>
            <span>{t("nav.brand")}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <a
                key={item.key}
                href={anchorHref(item.href)}
                data-testid={`nav-link-${item.key}`}
                className="text-sm font-medium text-slate-300 hover:text-white"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div
              role="group"
              aria-label={t("language.switcher")}
              className="hidden sm:flex items-center rounded-md border border-slate-700 bg-slate-800 p-0.5 text-xs font-semibold"
            >
              <button
                type="button"
                data-testid="language-switcher-ec"
                onClick={() => switchTo("ec")}
                className={`px-2.5 py-1 rounded ${
                  lang === "ec"
                    ? "bg-teal-500 text-slate-950"
                    : "text-slate-300 hover:text-white"
                }`}
                aria-pressed={lang === "ec"}
                aria-label={t("language.labelEs")}
              >
                {t("language.es")}
              </button>
              <button
                type="button"
                data-testid="language-switcher-en"
                onClick={() => switchTo("en")}
                className={`px-2.5 py-1 rounded ${
                  lang === "en"
                    ? "bg-teal-500 text-slate-950"
                    : "text-slate-300 hover:text-white"
                }`}
                aria-pressed={lang === "en"}
                aria-label={t("language.labelEn")}
              >
                {t("language.en")}
              </button>
            </div>

            <a
              href={anchorHref("#newsletter")}
              data-testid="header-cta"
              className="hidden md:inline-flex items-center rounded-md bg-teal-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-teal-400"
            >
              {t("nav.cta")}
            </a>

            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-700 text-slate-200"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu-panel"
            className="lg:hidden pb-4 pt-2 border-t border-slate-800"
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((item) => (
                <a
                  key={item.key}
                  href={anchorHref(item.href)}
                  onClick={closeMenu}
                  data-testid={`mobile-nav-link-${item.key}`}
                  className="rounded-md px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  {t(`nav.${item.key}`)}
                </a>
              ))}
              <a
                href={anchorHref("#newsletter")}
                onClick={closeMenu}
                data-testid="mobile-header-cta"
                className="mt-2 inline-flex items-center justify-center rounded-md bg-teal-500 px-4 py-2 text-sm font-semibold text-slate-950"
              >
                {t("nav.cta")}
              </a>
              <div className="mt-3 flex items-center gap-2 px-3">
                <span className="text-xs uppercase tracking-wider text-slate-400">
                  {t("language.switcher")}
                </span>
                <button
                  type="button"
                  data-testid="mobile-language-switcher-ec"
                  onClick={() => {
                    switchTo("ec");
                    closeMenu();
                  }}
                  className={`px-2.5 py-1 rounded text-xs font-semibold ${
                    lang === "ec"
                      ? "bg-teal-500 text-slate-950"
                      : "border border-slate-700 text-slate-200"
                  }`}
                >
                  {t("language.es")}
                </button>
                <button
                  type="button"
                  data-testid="mobile-language-switcher-en"
                  onClick={() => {
                    switchTo("en");
                    closeMenu();
                  }}
                  className={`px-2.5 py-1 rounded text-xs font-semibold ${
                    lang === "en"
                      ? "bg-teal-500 text-slate-950"
                      : "border border-slate-700 text-slate-200"
                  }`}
                >
                  {t("language.en")}
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
